import React, { useState, useEffect, useCallback } from 'react';
import { AlertTriangle, CreditCard, X, Clock } from 'lucide-react';
import { useAuth } from '../../context/AuthContext';
import { toast } from 'sonner';

/**
 * Top-of-page banner that surfaces payment failures.
 * Visible when workspace.payment_status in {grace, failed}.
 * In grace → amber with countdown. In failed/suspended → red.
 */
export default function PaymentAlertBanner({ workspaceId }) {
  const { api } = useAuth();
  const [state, setState] = useState(null); // { payment_status, grace_period_ends_at, name }
  const [dismissed, setDismissed] = useState(false);
  const [opening, setOpening] = useState(false);

  const fetchStatus = useCallback(async () => {
    if (!workspaceId) return;
    try {
      const res = await api.get(`/api/workspaces/${workspaceId}/billing`);
      const ps = res.data?.payment_status;
      if (ps === 'grace' || ps === 'failed') {
        setState({
          payment_status: ps,
          grace_period_ends_at: res.data?.grace_period_ends_at,
          status: res.data?.status,
        });
      } else {
        setState(null);
      }
    } catch {
      /* silent */
    }
  }, [api, workspaceId]);

  useEffect(() => {
    fetchStatus();
    const iv = setInterval(fetchStatus, 60_000); // 1 min
    return () => clearInterval(iv);
  }, [fetchStatus]);

  const openPortal = async () => {
    setOpening(true);
    try {
      const res = await api.post(`/api/workspaces/${workspaceId}/stripe/customer-portal`);
      if (res.data?.url) {
        window.location.href = res.data.url;
      } else {
        toast.error('Could not open billing portal');
      }
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Failed to open billing portal');
    } finally {
      setOpening(false);
    }
  };

  if (!state || dismissed) return null;

  const isGrace = state.payment_status === 'grace';
  const daysLeft = state.grace_period_ends_at
    ? Math.max(0, Math.ceil((new Date(state.grace_period_ends_at) - Date.now()) / 86400000))
    : 0;

  const cls = isGrace
    ? 'border-amber-400/30 bg-amber-400/10 text-amber-200'
    : 'border-rose-400/30 bg-rose-400/10 text-rose-200';

  return (
    <div
      className={`mx-4 md:mx-6 mt-4 rounded-xl border ${cls} flex items-center gap-3 px-4 py-3`}
      data-testid="payment-alert-banner"
    >
      <div className={`w-8 h-8 rounded-lg flex items-center justify-center ${isGrace ? 'bg-amber-400/15' : 'bg-rose-400/15'} flex-shrink-0`}>
        <AlertTriangle size={16} className={isGrace ? 'text-amber-400' : 'text-rose-400'} />
      </div>
      <div className="flex-1 min-w-0">
        <div className="text-sm font-semibold">
          {isGrace ? 'Payment failed — action required' : 'Workspace suspended due to failed payment'}
        </div>
        <div className="text-xs opacity-80 flex items-center gap-2 flex-wrap">
          {isGrace ? (
            <>
              <Clock size={12} />
              <span>
                Grace period ends in <span className="font-semibold">{daysLeft} day{daysLeft === 1 ? '' : 's'}</span>. Update your card to avoid service suspension.
              </span>
            </>
          ) : (
            <span>Update your payment method and contact support to reactivate.</span>
          )}
        </div>
      </div>
      <button
        onClick={openPortal}
        disabled={opening}
        className={`flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-semibold ${isGrace ? 'bg-amber-400 text-[#0a0b0f] hover:bg-amber-500' : 'bg-rose-400 text-white hover:bg-rose-500'} transition-colors flex-shrink-0 disabled:opacity-50`}
        data-testid="payment-alert-cta"
      >
        <CreditCard size={14} />
        {opening ? 'Opening…' : 'Update Payment'}
      </button>
      <button
        onClick={() => setDismissed(true)}
        className="p-1.5 rounded-lg opacity-60 hover:opacity-100 flex-shrink-0"
        title="Dismiss for this session"
        data-testid="payment-alert-dismiss"
      >
        <X size={14} />
      </button>
    </div>
  );
}
